import { useCallback } from 'react';

import saveFile from 'helpers/saveFile';
import moment from 'moment';
import { FilterMatchMode } from 'primereact/api';

import {
  AbsenceService,
  SchedulePersonShiftService
} from '../../../components/services';
import { TaskService } from '../../../components/services/TaskService';
import {
  CalendarAssignmentEmployee,
  PersonAbsence,
  PersonAbsencesToDelete,
  selectedDate,
  Shift,
  ShiftInterfere,
  ShiftToAdd,
  TreeNode
} from '../../../types/interfaces';

export interface RuleValidationError {
  errorCode: string;
  settingValue?: number;
  employees: { id: number; label: string }[];
}

export interface UseCalendarAssignmentsApiProps {
  selectedDepartmentFilter: TreeNode | null;
  unassignedTasksDateFilter: selectedDate;
  setUnassignedTasksCount: React.Dispatch<React.SetStateAction<number>>;

  toast: React.RefObject<any>;
  setLoading: (v: boolean) => void;
  getAssignmentCallback: () => Promise<void>;
  fetchPersonAbsences: () => Promise<void>;

  deletionParams: {
    startDate: Date;
    endDate: Date;
    selectedEmployees: CalendarAssignmentEmployee[];
  } | null;
  setConfirmDeletionDialogVisible: (v: boolean) => void;

  deletionAbsenceParams: {
    startDate: Date;
    endDate: Date;
    selectedEmployees: CalendarAssignmentEmployee[];
  } | null;
  setConfirmAbsenceDeletionDialogVisible: (v: boolean) => void;

  recalcParams: {
    startDate: Date;
    endDate: Date;
    selectedEmployees: CalendarAssignmentEmployee[];
    shouldClear: boolean;
    shouldDeleteAbsences: boolean;
  } | null;
  setConfirmRecalcDialogVisible: (v: boolean) => void;

  setNewShifts: (v: Shift[]) => void;
  setUpdatedShifts: (v: Shift[]) => void;
  setDeletedShiftIds: (v: string[]) => void;
  setHasUnsavedChanges: (v: boolean) => void;

  setRuleValidationErrors: React.Dispatch<
    React.SetStateAction<RuleValidationError[]>
  >;
  setRuleValidationDialogVisible: (v: boolean) => void;

  setValidationErrors: React.Dispatch<React.SetStateAction<ShiftInterfere[]>>;
  setValidationDialogVisible: (v: boolean) => void;

  setValidationErrorsImport: React.Dispatch<
    React.SetStateAction<{ error: string }[]>
  >;
  setErrorDialogVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useCalendarAssignmentsApi(props: UseCalendarAssignmentsApiProps) {
  const {
    selectedDepartmentFilter,
    unassignedTasksDateFilter,
    setUnassignedTasksCount,
    toast,
    setLoading,
    getAssignmentCallback,
    fetchPersonAbsences,
    deletionParams,
    setConfirmDeletionDialogVisible,
    deletionAbsenceParams,
    setConfirmAbsenceDeletionDialogVisible,
    recalcParams,
    setConfirmRecalcDialogVisible,
    setNewShifts,
    setUpdatedShifts,
    setDeletedShiftIds,
    setHasUnsavedChanges,
    setRuleValidationErrors,
    setRuleValidationDialogVisible,
    setValidationErrors,
    setValidationDialogVisible,
    setValidationErrorsImport,
    setErrorDialogVisible
  } = props;

  const fetchUnassignedTasksCount = useCallback(async () => {
    const deptKey = selectedDepartmentFilter?.key;
    const deptId = deptKey ? Number(deptKey) : NaN;
    if (!deptKey || isNaN(deptId)) {
      setUnassignedTasksCount(0);
      return;
    }
    try {
      const filters = JSON.stringify([
        {
          field: 'department',
          value: String(deptId),
          operator: FilterMatchMode.IN
        },
        {
          field: 'creationDateBetween',
          value: unassignedTasksDateFilter.dateFrom
            ? new Date(parseInt(unassignedTasksDateFilter.dateFrom)).toISOString()
            : '',
          additionalValue: unassignedTasksDateFilter.dateUntil
            ? new Date(parseInt(unassignedTasksDateFilter.dateUntil)).toISOString()
            : '',
          operator: FilterMatchMode.BETWEEN
        }
      ]);
      const resp = await TaskService.getPersonAssignment({
        sortBy: 'creationDate',
        sortDirection: 'desc',
        filters
      });
      const value = resp.data?.value;
      const count =
        typeof (value as any)?.total === 'number'
          ? (value as any).total
          : Array.isArray((value as any)?.itemsList)
          ? (value as any).itemsList.length
          : Array.isArray(value)
          ? value.length
          : 0;
      setUnassignedTasksCount(count);
    } catch (e) {
      setUnassignedTasksCount(0);
    }
  }, [
    selectedDepartmentFilter,
    unassignedTasksDateFilter.dateFrom,
    unassignedTasksDateFilter.dateUntil,
    setUnassignedTasksCount
  ]);

  const handleAfterUnassignedAssign = useCallback(async () => {
    await Promise.all([fetchUnassignedTasksCount(), getAssignmentCallback()]);
  }, [fetchUnassignedTasksCount, getAssignmentCallback]);

  const confirmMassDelete = useCallback(async () => {
    if (!deletionParams) return;
    try {
      setLoading(true);
      // Формируем данные для удаления
      const shiftsToDelete: ShiftToAdd[] = deletionParams.selectedEmployees.map(
        (emp) => ({
          personId: emp.id,
          departmentId: Number(selectedDepartmentFilter?.key),
          startDate: moment(deletionParams.startDate).format('YYYY-MM-DD'),
          endDate: moment(deletionParams.endDate).format('YYYY-MM-DD')
        })
      );
      await SchedulePersonShiftService.deleteShiftsInRange(shiftsToDelete);
      // Обновляем данные
      await getAssignmentCallback();
      setConfirmDeletionDialogVisible(false);
      toast.current?.show({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Смены удалены',
        life: 3000
      });
    } catch (error) {
      console.error(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось удалить смены',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  }, [
    deletionParams,
    getAssignmentCallback,
    selectedDepartmentFilter?.key,
    setConfirmDeletionDialogVisible,
    setLoading,
    toast
  ]);

  const confirmMassDeleteAbsences = useCallback(async () => {
    if (!deletionAbsenceParams) return;

    try {
      setLoading(true);
      const absencesToDelete: PersonAbsencesToDelete = {
        departmentId: Number(selectedDepartmentFilter?.key),
        personIds: deletionAbsenceParams.selectedEmployees.map((e) => e.id),
        from: moment(deletionAbsenceParams.startDate).format('YYYY-MM-DD'),
        to: moment(deletionAbsenceParams.endDate).format('YYYY-MM-DD')
      };

      await AbsenceService.deletePersonAbsencesInRange(absencesToDelete);
      await fetchPersonAbsences(); // Обновляем данные об отсутствиях

      setConfirmAbsenceDeletionDialogVisible(false);
      toast.current?.show({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Отсутствия удалены',
        life: 3000
      });
    } catch (error) {
      console.error(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось удалить отсутствия',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  }, [
    deletionAbsenceParams,
    fetchPersonAbsences,
    selectedDepartmentFilter?.key,
    setConfirmAbsenceDeletionDialogVisible,
    setLoading,
    toast
  ]);

  const isRuleValidationError = (error: any): error is RuleValidationError =>
    error &&
    typeof error === 'object' &&
    'errorCode' in error &&
    Array.isArray(error.employees);

  const handleRecalculate = useCallback(async () => {
    if (!recalcParams) return;

    try {
      setLoading(true);

      // Если нужно удалить отсутствия перед планированием
      if (recalcParams.shouldDeleteAbsences) {
        const absencesToDelete: PersonAbsencesToDelete = {
          departmentId: Number(selectedDepartmentFilter?.key),
          personIds: recalcParams.selectedEmployees.map((e) => e.id),
          from: moment(recalcParams.startDate).format('YYYY-MM-DD'),
          to: moment(recalcParams.endDate).format('YYYY-MM-DD')
        };
        await AbsenceService.deletePersonAbsencesInRange(absencesToDelete);
      }

      // Если нужно очистить график перед планированием
      if (recalcParams.shouldClear) {
        const shiftsToDelete: ShiftToAdd[] = recalcParams.selectedEmployees.map(
          (emp) => ({
            personId: emp.id,
            departmentId: Number(selectedDepartmentFilter?.key),
            startDate: moment(recalcParams.startDate).format('YYYY-MM-DD'),
            endDate: moment(recalcParams.endDate).format('YYYY-MM-DD')
          })
        );
        await SchedulePersonShiftService.deleteShiftsInRange(shiftsToDelete);
      }

      const shiftsToAdd: ShiftToAdd[] = recalcParams.selectedEmployees.map(
        (emp) => ({
          personId: emp.id,
          departmentId: Number(selectedDepartmentFilter?.key),
          startDate: moment(recalcParams.startDate).format('YYYY-MM-DD'),
          endDate: moment(recalcParams.endDate).format('YYYY-MM-DD')
        })
      );

      let validationResponse;
      try {
        validationResponse =
          await SchedulePersonShiftService.validateShiftCalc(shiftsToAdd);
      } catch (error: any) {
        // Если пришла HTTP ошибка, пытаемся извлечь данные из ответа
        // Interceptor может вернуть error.response, поэтому проверяем оба варианта
        if (error?.response?.data) {
          validationResponse = error.response;
        } else if (error?.data) {
          // Если interceptor вернул error.response напрямую
          validationResponse = { data: error.data, status: error.status };
        } else {
          // Если это не ошибка с данными, пробрасываем дальше
          throw error;
        }
      }

      // Проверяем, что ответ не является ошибкой без данных
      if (
        !validationResponse ||
        (!validationResponse.data &&
          validationResponse.status &&
          validationResponse.status >= 400)
      ) {
        throw new Error('Ошибка валидации без данных');
      }

      // Извлекаем данные из ответа (может быть обернуто в ApiResponse или быть массивом напрямую)
      let rawErrors: any[] = [];
      if (validationResponse?.data) {
        if (Array.isArray(validationResponse.data)) {
          // Если ответ - это массив ошибок
          rawErrors = validationResponse.data;
        } else if (
          validationResponse.data.value &&
          Array.isArray(validationResponse.data.value)
        ) {
          // Если ответ обернут в ApiResponse
          rawErrors = validationResponse.data.value;
        } else if (validationResponse.data.errorCode) {
          // Если пришел один объект ошибки, оборачиваем в массив
          rawErrors = [validationResponse.data];
        } else if (Array.isArray(validationResponse.data.errors)) {
          // Если ошибки в поле errors
          rawErrors = validationResponse.data.errors;
        }
      }

      // Если ошибок нет, но есть данные, возможно это массив с одним элементом
      if (
        rawErrors.length === 0 &&
        validationResponse?.data &&
        typeof validationResponse.data === 'object'
      ) {
        // Проверяем, может быть это массив, который не был распознан
        const data = validationResponse.data;
        if (data.errorCode && data.employees) {
          rawErrors = [data];
        }
      }

      const ruleErrors = rawErrors.filter((err) =>
        isRuleValidationError(err)
      ) as RuleValidationError[];
      const conflictErrors = rawErrors.filter(
        (err) => !isRuleValidationError(err)
      ) as ShiftInterfere[];

      let remainingShifts = shiftsToAdd;
      const blockedIds = new Set<number>();

      if (ruleErrors.length > 0) {
        ruleErrors.forEach((error) => {
          (error.employees || []).forEach((employee) => {
            blockedIds.add(employee.id);
          });
        });

        remainingShifts = remainingShifts.filter(
          (shift) => !blockedIds.has(shift.personId)
        );

        // Показываем диалог с ошибками правил
        setRuleValidationErrors(ruleErrors);
        setRuleValidationDialogVisible(true);
      }

      if (conflictErrors.length > 0) {
        const conflictPersonIds = new Set(
          conflictErrors.map((error) => error.personId)
        );

        // Исключаем сотрудников с конфликтами из планирования
        remainingShifts = remainingShifts.filter(
          (shift) => !conflictPersonIds.has(shift.personId)
        );

        // Показываем диалог с конфликтами смен
        setValidationErrors(conflictErrors);
        setValidationDialogVisible(true);
      }

      // Планируем оставшихся сотрудников
      if (remainingShifts.length > 0) {
        await SchedulePersonShiftService.createPersonsShifts(remainingShifts);
      }

      await getAssignmentCallback();
      setConfirmRecalcDialogVisible(false);
      setNewShifts([]);
      setUpdatedShifts([]);
      setDeletedShiftIds([]);
      setHasUnsavedChanges(false);

      // Показываем сообщение об успехе только если нет ошибок
      if (ruleErrors.length === 0 && conflictErrors.length === 0) {
        toast.current?.show({
          severity: 'success',
          summary: 'Успешно',
          detail: 'График пересчитан',
          life: 3000
        });
      } else if (remainingShifts.length > 0) {
        // Если есть ошибки, но часть сотрудников спланирована
        toast.current?.show({
          severity: 'success',
          summary: 'Успешно',
          detail: 'График пересчитан частично',
          life: 3000
        });
      }
    } catch (error) {
      console.error(error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось пересчитать график',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  }, [
    getAssignmentCallback,
    recalcParams,
    selectedDepartmentFilter?.key,
    setConfirmRecalcDialogVisible,
    setDeletedShiftIds,
    setHasUnsavedChanges,
    setLoading,
    setNewShifts,
    setRuleValidationDialogVisible,
    setRuleValidationErrors,
    setUpdatedShifts,
    setValidationDialogVisible,
    setValidationErrors,
    toast
  ]);

  const importAbsencesXLS = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);

      try {
        // Validate the file
        const validationResponse = await AbsenceService.importAbsences(
          formData,
          true
        );
        if (validationResponse?.data?.errors?.length > 0) {
          setValidationErrorsImport(
            validationResponse.data.errors.map((i: string) => ({ error: i }))
          );
          setErrorDialogVisible(true);
          return;
        }

        // Import the file (PUT)
        const importResponse = await AbsenceService.importAbsences(formData);
        toast.current?.show({
          severity: 'success',
          summary: 'Импорт успешно завершен',
          detail: `${importResponse.data.absences.length} отсутствий импортировано.`,
          sticky: true
        });

        // Optionally reload data or trigger a refresh
        await getAssignmentCallback();
      } catch (error) {
        console.error('Ошибка импорта:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка импорта',
          detail: 'Произошла ошибка при импорте файла.',
          sticky: true
        });
      }
    },
    [
      getAssignmentCallback,
      setErrorDialogVisible,
      setValidationErrorsImport,
      toast
    ]
  );

  const exportTemplate = useCallback(async () => {
    const result = await AbsenceService.getAbsencesTemplateExcel();
    await saveFile(result.data, 'Шаблон.xls', '');
  }, []);

  return {
    fetchUnassignedTasksCount,
    handleAfterUnassignedAssign,
    confirmMassDelete,
    confirmMassDeleteAbsences,
    handleRecalculate,
    importAbsencesXLS,
    exportTemplate
  };
}

